import { IonButton, IonCard, IonCardContent, IonCol, IonLoading, IonRow } from '@ionic/react';
import { DqText } from '../../components/dq-text';
import { JobReportGuestAuthMessages } from '../../intl/JobReportGuestAuthMessages';
import { Input } from '../../components/reusable-components/Input';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Auth } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../redux/types';
import { AnyAction } from 'redux';
import { fetchGuestInfo } from '../../redux/auth/thunk/fetchGuestInfo';
import { authActions } from '../../redux/auth/actions';
import { CognitoUser } from '@aws-amplify/auth';
import { OneButtonModal, OneButtonModalContent } from '../../components/OneButtonModal';
import { NotGuestComponent } from './NotGuestComponent';
import { selectAuthUserInfo } from '../../redux/auth/selectors';
import { selectAuthState } from '../../redux/auth/selectors';

export interface GuestSignInCardProps {
  cognitoUser: CognitoUser;
  onLoginSuccess: () => void;
  emailAddress: string;
}

/**
 * Card that allows the user to enter the security code that was generated and
 * sent to them.
 *
 * Optionally allows user to sign in with username and password.
 *
 * @param cognitoUser
 * @param onLoginSuccess
 * @param emailAddress
 * @constructor
 */
export const GuestSignInCard: FC<GuestSignInCardProps> = ({ cognitoUser, onLoginSuccess, emailAddress }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  const [securityCode, setSecurityCode] = useState<string | undefined>(undefined);
  const [awaitingLoginResult, setAwaitingLoginResult] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<OneButtonModalContent>(['', '']);
  const userInfo = useSelector(selectAuthUserInfo);
  const authState = useSelector(selectAuthState);

  const onSecurityCodeInputChange = useCallback(
    (event: CustomEvent) => {
      if (event.detail.value?.length > 5) {
        setSecurityCode(event.detail.value);
      } else if (securityCode) {
        setSecurityCode(undefined);
      }
    },
    [securityCode],
  );

  const onLoginButtonClicked = React.useCallback(async () => {
    if (!securityCode) {
      return;
    }

    setAwaitingLoginResult(true);
    Auth.sendCustomChallengeAnswer(cognitoUser, securityCode)
      .then(_ => {
        Auth.currentSession()
          .then(session => {
            console.log('[GuestSignInCard] session is valid:', session.isValid());
            const issuedAt = session.getAccessToken().getIssuedAt() * 1000;
            dispatch(authActions.updateTokenIssuedAt({ issuedAt }));
            dispatch(fetchGuestInfo());
          })
          .catch(err => {
            console.log('[GuestSignInCard] Auth.currentSession failed', err);
            setAwaitingLoginResult(false);
            const errorTitle = formatMessage(JobReportGuestAuthMessages.incorrectCodeTitle);
            const errorMessage = formatMessage(JobReportGuestAuthMessages.incorrectCodeMessage);
            setModalContent([errorTitle, errorMessage]);
          });
      })
      .catch(err => {
        console.log('[GuestSignInCard] Auth.sendCustomChallengeAnswer failed:', err);
        setAwaitingLoginResult(false);
        const errorTitle = formatMessage(JobReportGuestAuthMessages.expiredCodeTitle);
        const errorMessage = formatMessage(JobReportGuestAuthMessages.expiredCodeMessage);
        setModalContent([errorTitle, errorMessage]);
      });
  }, [cognitoUser, dispatch, formatMessage, securityCode]);

  // We dispatched fetchGuestInfo when session is valid above. Show the loading component until that fetch completes,
  // and then tell our parent that sign in was successful to that it can navigate as needed. OTOH, if we can't fetch
  // user data, display a modal with an error message. One way to force this error is to shut down the portal api.
  useEffect(() => {
    if (authState.user) {
      setAwaitingLoginResult(false);
      onLoginSuccess();
      return;
    }

    if (authState.authSucceeded === false) {
      setAwaitingLoginResult(false);
      const errorTitle = formatMessage(JobReportGuestAuthMessages.internalErrorTitle);
      const errorMessage = formatMessage(JobReportGuestAuthMessages.internalErrorMessage);
      setModalContent([errorTitle, errorMessage]);
    }
  }, [authState.authSucceeded, authState.user, formatMessage, onLoginSuccess, userInfo]);

  return (
    <IonCard className="content-card">
      <IonLoading isOpen={awaitingLoginResult} spinner="circles" />
      <OneButtonModal
        isOpen={modalContent[0].length > 0}
        onClose={() => setModalContent(['', ''])}
        content={modalContent}
      />
      <IonCardContent>
        <IonRow>
          <DqText className="guest-sign-in-title" variant="h3" color="dark">
            {formatMessage(JobReportGuestAuthMessages.guestLogInTitle)}
          </DqText>
        </IonRow>
        <IonRow>
          <DqText variant="body" color="dark">
            {formatMessage(JobReportGuestAuthMessages.codeHasBeenEmailedMessage, {
              emailAddress: emailAddress,
            })}
          </DqText>
        </IonRow>
        <IonRow>
          <IonCol size="5">
            <Input
              label={formatMessage(JobReportGuestAuthMessages.emailCodeLabel)}
              id="security-code-input"
              value={securityCode}
              onChange={onSecurityCodeInputChange}
            />
          </IonCol>
          <IonCol className="guest-sign-in-button" size="3">
            <IonButton
              id="guest-sign-in-button"
              onClick={onLoginButtonClicked}
              fill="solid"
              color="primary"
              disabled={securityCode === undefined}
            >
              {formatMessage(JobReportGuestAuthMessages.signInButtonLabel)}
            </IonButton>
          </IonCol>
        </IonRow>
        <IonRow className="top-border" />
        <NotGuestComponent />
      </IonCardContent>
    </IonCard>
  );
};
